import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  TableIconButton,
  TableIconLink,
} from '../../components/TableIconAction';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, SortState } from '../../components/SortableTh';
import {
  Badge,
  BanIcon,
  Button,
  Callout,
  CheckCircleIcon,
  ClockIcon,
  DateInput,
  DocumentTextIcon,
  ListEmptyState,
  ListPagination,
  LoadingSpinner,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  SegmentedControl,
  Select,
  TableIcon,
  TextInput,
  XIcon,
  useConfirm,
} from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import {
  CompanyExpenseCategory,
  CompanyExpenseListItem,
  CompanyExpenseStatus,
} from '../../types/company-expense';
import { formatDate, formatMoney } from '../../utils/format';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SERVER_SORT_KEYS = new Set([
  'expenseDate',
  'dueDate',
  'vendorName',
  'amount',
]);

type StatusFilter = 'all' | 'open' | 'unpaid' | 'partial' | 'paid';
const PILL_ICON_CLASS = 'h-3.5 w-3.5';

function statusPills() {
  const filters = t().companyExpenses.filters;
  return [
    {
      id: 'all' as const,
      label: filters.all,
      icon: <TableIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'open' as const,
      label: filters.open,
      icon: <DocumentTextIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'unpaid' as const,
      label: filters.unpaid,
      icon: <ClockIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'partial' as const,
      label: filters.partial,
      icon: <ClockIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'paid' as const,
      label: filters.paid,
      icon: <CheckCircleIcon className={PILL_ICON_CLASS} />,
    },
  ];
}

const sortGetters: Record<
  string,
  (item: CompanyExpenseListItem) => string | number | boolean | null | undefined
> = {
  expenseDate: (i) => i.expenseDate,
  dueDate: (i) => i.dueDate,
  vendorName: (i) => i.vendorName,
  category: (i) => i.category.name,
  amount: (i) => i.amount.gross,
  remaining: (i) => i.remaining.gross,
  status: (i) => i.status,
};

function statusBadge(status: CompanyExpenseStatus) {
  const labels = t().companyExpenses.statuses;
  if (status === 'PAID') {
    return <Badge variant="success">{labels.PAID}</Badge>;
  }
  if (status === 'PARTIAL') {
    return <Badge variant="warning">{labels.PARTIAL}</Badge>;
  }
  return <Badge variant="danger">{labels.UNPAID}</Badge>;
}

export function CompanyExpenseListPage() {
  const labels = t().companyExpenses;
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<CompanyExpenseListItem[]>([]);
  const [categories, setCategories] = useState<CompanyExpenseCategory[]>([]);
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: 20,
    pageCount: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const search = searchParams.get('q') ?? '';
  const status = (searchParams.get('status') as StatusFilter) || 'all';
  const categoryId = searchParams.get('categoryId') ?? '';
  const type = searchParams.get('type') ?? '';
  const dateFrom = searchParams.get('dateFrom') ?? '';
  const dateTo = searchParams.get('dateTo') ?? '';
  const page = Number(searchParams.get('page') || 1);
  const pageSize = Number(searchParams.get('pageSize') || 20);
  const sortBy = searchParams.get('sortBy') || 'expenseDate';
  const sortDir = (searchParams.get('sortDir') as 'asc' | 'desc') || 'desc';
  const sort: SortState = { key: sortBy, direction: sortDir };

  const displayItems = useMemo(() => {
    if (SERVER_SORT_KEYS.has(sort.key)) return items;
    return sortItems(items, sort, sortGetters);
  }, [items, sort]);

  function updateParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value == null || value === '') next.delete(key);
      else next.set(key, value);
    }
    if (!('page' in patch)) next.set('page', '1');
    setSearchParams(next);
  }

  useEffect(() => {
    void api.getCompanyExpenseCategories(false).then(setCategories);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getCompanyExpenses({
          search: search || undefined,
          status: status === 'all' ? undefined : status,
          categoryId: categoryId ? Number(categoryId) : undefined,
          type: type || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          page,
          pageSize,
          sortBy: SERVER_SORT_KEYS.has(sortBy) ? sortBy : 'expenseDate',
          sortDir,
        });
        if (cancelled) return;
        setItems(result.data);
        setMeta(result.meta);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [
    search,
    status,
    categoryId,
    type,
    dateFrom,
    dateTo,
    page,
    pageSize,
    sortBy,
    sortDir,
  ]);

  function toggleSort(key: string) {
    const nextDir =
      sort.key === key && sort.direction === 'asc' ? 'desc' : 'asc';
    updateParams({ sortBy: key, sortDir: nextDir, page: '1' });
  }

  async function handleDelete(id: number) {
    const ok = await confirm({
      title: labels.deleteConfirmTitle,
      message: labels.deleteConfirmMessage,
      confirmText: labels.deleteConfirmTitle,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setError(null);
    setMessage(null);
    try {
      await api.deleteCompanyExpense(id);
      setMessage(labels.deleteSuccess);
      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  const isAdmin = user?.role === 'ADMIN';

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <Button
            type="button"
            size="sm"
            onClick={() => navigate('/company-expenses/new')}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            {labels.new}
          </Button>
        }
      >
        <FilterField label={t().common.search} className="min-w-[14rem] flex-1">
          <TextInput
            value={search}
            onChange={(e) => updateParams({ q: e.target.value || null })}
            placeholder={labels.searchPlaceholder}
            prefix={<SearchIcon className="h-4 w-4" />}
          />
        </FilterField>
        <FilterField label={labels.category}>
          <Select
            value={categoryId}
            size="sm"
            showClearButton={Boolean(categoryId)}
            placeholder={labels.category}
            options={categories.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            onChange={(e) =>
              updateParams({ categoryId: e.target.value || null })
            }
          />
        </FilterField>
        <FilterField label={labels.type}>
          <Select
            value={type}
            size="sm"
            showClearButton={Boolean(type)}
            placeholder={labels.type}
            options={[
              { value: 'INVOICE', label: t().financial.invoice },
              { value: 'CASH', label: t().financial.cash },
            ]}
            onChange={(e) => updateParams({ type: e.target.value || null })}
          />
        </FilterField>
        <FilterField label={labels.expenseDate}>
          <div className="flex gap-2">
            <DateInput
              value={dateFrom}
              onChange={(e) =>
                updateParams({ dateFrom: e.target.value || null })
              }
            />
            <DateInput
              value={dateTo}
              onChange={(e) => updateParams({ dateTo: e.target.value || null })}
            />
          </div>
        </FilterField>
      </ListFiltersBar>

      <div className="shrink-0">
        <SegmentedControl
          value={status}
          onChange={(id) => updateParams({ status: id === 'all' ? null : id })}
          size="sm"
          aria-label={labels.status}
          options={statusPills()}
        />
      </div>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}
      {message ? (
        <Callout variant="success" className="shrink-0">
          {message}
        </Callout>
      ) : null}

      <DataTablePanel
        footer={
          !loading && meta.total > 0 ? (
            <ListPagination
              page={meta.page}
              totalPages={meta.pageCount}
              total={meta.total}
              itemsPerPage={meta.pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={(next) => updateParams({ page: String(next) })}
              onItemsPerPageChange={(size) =>
                updateParams({ pageSize: String(size), page: '1' })
              }
            />
          ) : null
        }
      >
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : displayItems.length === 0 ? (
          <ListEmptyState title={labels.emptyTitle} hint={labels.emptyHint} />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="whitespace-nowrap px-3 py-2 font-medium">
                  {t().common.actions}
                </th>
                <SortableTh
                  sortKey="expenseDate"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {labels.expenseDate}
                </SortableTh>
                <SortableTh
                  sortKey="vendorName"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {labels.vendorName}
                </SortableTh>
                <SortableTh sortKey="category" sort={sort} onSort={toggleSort}>
                  {labels.category}
                </SortableTh>
                <SortableTh sortKey="dueDate" sort={sort} onSort={toggleSort}>
                  {labels.dueDate}
                </SortableTh>
                <SortableTh
                  sortKey="amount"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {labels.gross}
                </SortableTh>
                <SortableTh
                  sortKey="remaining"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {labels.remaining}
                </SortableTh>
                <SortableTh sortKey="status" sort={sort} onSort={toggleSort}>
                  {labels.status}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayItems.map((item) => (
                <tr key={item.id} className="border-t border-line">
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="inline-flex items-center gap-0.5">
                      <TableIconLink
                        to={`/company-expenses/${item.id}/edit`}
                        label={labels.edit}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </TableIconLink>
                      {isAdmin ? (
                        <TableIconButton
                          label={labels.deleteConfirmTitle}
                          variant="muted"
                          onClick={() => void handleDelete(item.id)}
                        >
                          <BanIcon className="h-4 w-4" />
                        </TableIconButton>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Link
                      to={`/company-expenses/${item.id}/edit`}
                      className="text-brand hover:underline"
                    >
                      {formatDate(item.expenseDate)}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    {item.vendorName || '—'}
                    {item.documentNo ? (
                      <div className="text-xs text-ink-faint">
                        {item.documentNo}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">{item.category.name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatDate(item.dueDate)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(item.amount.gross)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(item.remaining.gross)}
                  </td>
                  <td className="px-3 py-2">{statusBadge(item.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTablePanel>
    </PageShell>
  );
}
